<template>
  <div v-if="singleSelectField === null" class="kanban-view__stacked-by-page">
    <KanbanViewStackedBy
      :table="table"
      :view="view"
      :fields="fields"
      :database="database"
      :read-only="readOnly"
      :store-prefix="storePrefix"
      :include-field-options-on-refresh="true"
      @refresh="$emit('refresh', $event)"
    ></KanbanViewStackedBy>
  </div>
  <div
    v-else
    ref="kanban"
    v-auto-scroll="{
      orientation: 'horizontal',
      enabled: () => draggingRow !== null || draggingStackOption !== null,
      speed: 6,
      padding: 12,
    }"
    class="kanban-view"
  >
    <div class="kanban-view__stacks">
      <KanbanViewStack
        :database="database"
        :table="table"
        :view="view"
        :card-fields="cardFields"
        :fields="fields"
        :read-only="readOnly"
        :store-prefix="storePrefix"
        @create-row="openCreateRowModal"
        @edit-row="openRowEditModal($event)"
        @refresh="$emit('refresh', $event)"
        @row-context="showRowContext($event.event, $event.row)"
      ></KanbanViewStack>
      <KanbanViewStack
        v-for="option in existingSelectOption"
        :key="option.id"
        :option="option"
        :database="database"
        :table="table"
        :view="view"
        :card-fields="cardFields"
        :fields="fields"
        :read-only="readOnly"
        :store-prefix="storePrefix"
        @create-row="openCreateRowModal"
        @edit-row="openRowEditModal($event)"
        @refresh="$emit('refresh', $event)"
        @row-context="showRowContext($event.event, $event.row)"
        @stack-down="stackDown"
      ></KanbanViewStack>
      <div class="kanban-view__stack-wrapper">
        <div class="kanban-view__stack">
          <div class="kanban-view__stack-head">
            <Button
              v-if="
                !readOnly &&
                !singleSelectField.immutable_properties &&
                $hasPermission(
                  'database.table.field.update',
                  table,
                  database.workspace.id
                )
              "
              ref="addOptionContextLink"
              tag="a"
              type="secondary"
              size="regular"
              icon="iconoir-plus"
              :full-width="true"
              @click="
                $refs.addOptionContext.toggle(
                  $refs.addOptionContextLink.$el,
                  'bottom',
                  'right'
                )
              "
              >{{ $t('kanbanView.addStack') }}</Button
            >
            <KanbanViewCreateStackContext
              ref="addOptionContext"
              :fields="fields"
              :store-prefix="storePrefix"
            ></KanbanViewCreateStackContext>
          </div>
        </div>
      </div>
    </div>
    <RowCreateModal
      ref="rowCreateModal"
      :database="database"
      :table="table"
      :view="view"
      :primary-is-sortable="true"
      :visible-fields="cardFields"
      :hidden-fields="hiddenFields"
      :show-hidden-fields="showHiddenFieldsInRowModal"
      :all-fields-in-table="fields"
      @toggle-hidden-fields-visibility="
        showHiddenFieldsInRowModal = !showHiddenFieldsInRowModal
      "
      @created="createRow"
      @order-fields="orderFields"
      @toggle-field-visibility="toggleFieldVisibility"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
    ></RowCreateModal>
    <RowEditModal
      ref="rowEditModal"
      enable-navigation
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :primary-is-sortable="true"
      :visible-fields="cardFields"
      :hidden-fields="hiddenFields"
      :rows="allRows"
      :read-only="
        readOnly ||
        (!$hasPermission(
          'database.table.update_row',
          table,
          database.workspace.id
        ) &&
          !$hasPermission(
            'database.table.view.update_row',
            view,
            database.workspace.id
          ))
      "
      :show-hidden-fields="showHiddenFieldsInRowModal"
      @hidden="$emit('selected-row', undefined)"
      @toggle-hidden-fields-visibility="
        showHiddenFieldsInRowModal = !showHiddenFieldsInRowModal
      "
      @update="updateValue"
      @order-fields="orderFields"
      @toggle-field-visibility="toggleFieldVisibility"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="
        (fieldCreated($event), (showHiddenFieldsInRowModal = true))
      "
      @field-created-callback-done="afterFieldCreatedUpdateFieldOptions"
      @navigate-previous="$emit('navigate-previous', $event)"
      @navigate-next="$emit('navigate-next', $event)"
      @refresh-row="refreshRow"
    ></RowEditModal>
    <Context
      ref="cardContext"
      :overflow-scroll="true"
      :max-height-if-outside-viewport="true"
    >
      <ul class="context__menu">
        <TransferRowContextItem
          :database="database"
          :table="table"
          :row="selectedRow"
          :read-only="readOnly"
          @close-context="$refs.cardContext.hide()"
        />
        <li
          v-if="
            !readOnly &&
            ($hasPermission(
              'database.table.delete_row',
              table,
              database.workspace.id
            ) ||
              $hasPermission(
                'database.table.view.delete_row',
                view,
                database.workspace.id
              ))
          "
          class="context__menu-item"
        >
          <a
            class="context__menu-item-link js-ctx-delete-row"
            @click="deleteRow(selectedRow)"
          >
            <i class="context__menu-item-icon iconoir-bin"></i>
            {{ $t('gridView.deleteRow') }}
          </a>
        </li>
      </ul>
    </Context>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { clone } from '@baserow/modules/core/utils/object'
import {
  startPointerDrag,
  animateGhostTo,
} from '@baserow/modules/core/utils/dragAndDrop'
import { populateRow } from '@baserow/modules/database/store/view/grid'
import { notifyIf } from '@baserow/modules/core/utils/error'
import viewHelpers from '@baserow/modules/database/mixins/viewHelpers'
import {
  sortFieldsByOrderAndIdFunction,
  filterVisibleFieldsFunction,
  filterHiddenFieldsFunction,
} from '@baserow/modules/database/utils/view'
import RowCreateModal from '@baserow/modules/database/components/row/RowCreateModal'
import TransferRowContextItem from '@baserow/modules/database/components/row/TransferRowContextItem'
import RowEditModal from '@baserow/modules/database/components/row/RowEditModal'
import kanbanViewHelper from '@baserow_premium/mixins/kanbanViewHelper'
import KanbanViewStack from '@baserow_premium/components/views/kanban/KanbanViewStack'
import KanbanViewStackedBy from '@baserow_premium/components/views/kanban/KanbanViewStackedBy'
import KanbanViewCreateStackContext from '@baserow_premium/components/views/kanban/KanbanViewCreateStackContext'

export default {
  name: 'KanbanView',
  emits: ['navigate-next', 'navigate-previous', 'refresh', 'selected-row'],
  components: {
    TransferRowContextItem,
    RowCreateModal,
    RowEditModal,
    KanbanViewCreateStackContext,
    KanbanViewStackedBy,
    KanbanViewStack,
  },
  mixins: [viewHelpers, kanbanViewHelper],
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      showHiddenFieldsInRowModal: false,
      selectedRow: null,
      // Origem Flow: estado do arrasto de colunas (reordenação de opções).
      draggingStackOption: null,
      stackDragState: null,
    }
  },
  computed: {
    ...mapGetters({
      row: 'rowModalNavigation/getRow',
    }),
    /**
     * Returns the visible field objects in the right order.
     */
    cardFields() {
      const fieldOptions = this.fieldOptions
      return this.fields
        .filter(filterVisibleFieldsFunction(fieldOptions))
        .sort(sortFieldsByOrderAndIdFunction(fieldOptions))
    },
    hiddenFields() {
      const fieldOptions = this.fieldOptions
      return this.fields
        .filter(filterHiddenFieldsFunction(fieldOptions))
        .sort(sortFieldsByOrderAndIdFunction(fieldOptions))
    },
    /**
     * Returns the single select field object that the kanban view uses to workspace the
     * cards in stacks.
     */
    singleSelectField() {
      const allFields = this.fields
      for (let i = 0; i < allFields.length; i++) {
        if (allFields[i].id === this.singleSelectFieldId) {
          return allFields[i]
        }
      }
      return null
    },
    existingSelectOption() {
      return this.singleSelectField.select_options.filter((option) => {
        return this.$store.getters[
          `${this.storePrefix}view/kanban/stackExists`
        ](option.id)
      })
    },
    singleSelectFieldId() {
      return this.$store.getters[
        `${this.storePrefix}view/kanban/getSingleSelectFieldId`
      ]
    },
    allRows() {
      return this.$store.getters[`${this.storePrefix}view/kanban/getAllRows`]
    },
    draggingRow() {
      return this.$store.getters[
        `${this.storePrefix}view/kanban/getDraggingRow`
      ]
    },
    fieldOptions() {
      return this.$store.getters[
        `${this.storePrefix}view/kanban/getAllFieldOptions`
      ]
    },
  },
  watch: {
    row: {
      deep: true,
      handler(row, oldRow) {
        if (this.$refs.rowEditModal) {
          if (
            (oldRow === null && row !== null) ||
            (oldRow && row && oldRow.id !== row.id)
          ) {
            this.populateAndEditRow(row)
          } else if (oldRow !== null && row === null) {
            // Pass emit=false as argument into the hide function because that will
            // prevent emitting another `hidden` event of the `RowEditModal` which can
            // result in the route changing twice.
            this.$refs.rowEditModal.hide(false)
          }
        }
      },
    },
  },
  mounted() {
    if (this.row !== null) {
      this.populateAndEditRow(this.row)
    }
  },
  methods: {
    /**
     * When the row edit modal is opened we notify
     * the Table component that a new row has been selected,
     * such that we can update the path to include the row id.
     */
    openRowEditModal(row) {
      this.$refs.rowEditModal.show(row.id)
      this.$emit('selected-row', row)
    },
    openCreateRowModal(event) {
      const defaults = {}
      const name = `field_${this.singleSelectField.id}`
      if (event.option !== null) {
        defaults[name] = clone(event.option)
      } else {
        defaults[name] = null
      }

      this.$refs.rowCreateModal.show(defaults)
    },
    async createRow({ row, callback }) {
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/kanban/createNewRow',
          {
            view: this.view,
            table: this.table,
            fields: this.fields,
            values: row,
          }
        )
        callback()
      } catch (error) {
        callback(error)
      }
    },
    async updateValue({ field, row, value, oldValue }) {
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/kanban/updateRowValue',
          {
            table: this.table,
            view: this.view,
            fields: this.fields,
            row,
            field,
            value,
            oldValue,
          }
        )
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    /**
     * Calls action in the store to refresh row directly from the backend - f. ex.
     * when editing row from a different table, when editing is complete, we need
     * to refresh the 'main' row that's 'under' the RowEdit modal.
     */
    refreshRow(row) {
      if (this.refreshingRow) {
        return
      }
      this.refreshingRow = true
      this.$nextTick(async () => {
        try {
          await this.$store.dispatch(
            this.storePrefix + 'view/kanban/refreshRowFromBackend',
            { table: this.table, row }
          )
        } catch (error) {
          notifyIf(error, 'row')
        } finally {
          this.refreshingRow = false
        }
      })
    },
    /**
     * Populates a new row and opens the row edit modal
     * to edit the row.
     */
    populateAndEditRow(row) {
      const rowClone = populateRow(clone(row))
      this.$refs.rowEditModal.show(row.id, rowClone)
    },

    showRowContext(event, row) {
      this.selectedRow = row
      this.$refs.cardContext.toggleNextToMouse(event)
    },

    /**
     * Origem Flow: início do arrasto de uma coluna do kanban, disparado pelo
     * mousedown no cabeçalho da coluna (KanbanViewStack). A soltura reordena
     * as opções do campo single select, como o ClickUp faz com os status.
     */
    stackDown({ event, option, el }) {
      if (
        this.singleSelectField.immutable_properties ||
        !this.$hasPermission(
          'database.table.field.update',
          this.table,
          this.database.workspace.id
        )
      ) {
        return
      }

      if (event.pointerType !== 'touch') {
        event.preventDefault()
      }

      this.stackDragState = {
        option,
        originEl: el,
        grabOffsetX: 0,
        grabOffsetY: 0,
        ghost: null,
        indicator: null,
        targetIndex: null,
      }
      this.stackDragController = startPointerDrag(event, {
        onStart: (startEvent) => this.stackDragStart(startEvent),
        onMove: (moveEvent) => this.stackDragMove(moveEvent),
        onUp: (upEvent, engaged) => {
          if (engaged) {
            this.stackDragUp()
          } else {
            this.stackDragCancel()
          }
        },
        onCancel: () => this.stackDragCancel(),
      })
    },
    stackDragStart(event) {
      const state = this.stackDragState
      if (state === null) {
        return
      }
      this.draggingStackOption = state.option

      const rect = state.originEl.getBoundingClientRect()
      state.grabOffsetX = event.clientX - rect.left
      state.grabOffsetY = event.clientY - rect.top

      const ghost = state.originEl.cloneNode(true)
      ghost.classList.add('kanban-view__stack-drag-ghost')
      ghost.style.width = rect.width + 'px'
      ghost.style.height = rect.height + 'px'
      document.body.appendChild(ghost)
      state.ghost = ghost

      state.originEl.classList.add('kanban-view__stack-wrapper--drag-origin')

      const indicator = document.createElement('div')
      indicator.classList.add('kanban-view__stack-drop-indicator')
      this.getStacksElement().appendChild(indicator)
      state.indicator = indicator

      this.stackDragMove(event)
    },
    stackDragMove(event) {
      const state = this.stackDragState
      if (state === null || state.ghost === null) {
        return
      }

      state.ghost.style.left = event.clientX - state.grabOffsetX + 'px'
      state.ghost.style.top = event.clientY - state.grabOffsetY + 'px'

      // O índice de inserção é encontrado comparando o cursor com o centro de
      // cada coluna de opção. O primeiro wrapper é a coluna "sem estado" e o
      // último é o botão de adicionar coluna, por isso o slice.
      const stacksEl = this.getStacksElement()
      const optionWrappers = this.getOptionWrappers(stacksEl)
      let index = optionWrappers.length
      for (let i = 0; i < optionWrappers.length; i++) {
        const r = optionWrappers[i].getBoundingClientRect()
        if (event.clientX < r.left + r.width / 2) {
          index = i
          break
        }
      }
      state.targetIndex = index

      const stacksRect = stacksEl.getBoundingClientRect()
      let left
      if (index < optionWrappers.length) {
        // Centralizado no espaçamento de 20px à esquerda da coluna alvo.
        left =
          optionWrappers[index].getBoundingClientRect().left -
          stacksRect.left +
          8
      } else {
        const lastEl = optionWrappers[optionWrappers.length - 1]
        left = lastEl.getBoundingClientRect().right - stacksRect.left + 8
      }
      state.indicator.style.left = left + 'px'
    },
    async stackDragUp() {
      const state = this.stackDragState
      if (state === null) {
        return
      }
      const { option, targetIndex, ghost, indicator, originEl } = state

      // Origem Flow: animação de pouso — o clone da coluna desliza até a
      // linha indicadora antes da limpeza.
      if (ghost && indicator) {
        const indicatorRect = indicator.getBoundingClientRect()
        const originRect = originEl.getBoundingClientRect()
        await animateGhostTo(ghost, {
          left: indicatorRect.left - 8,
          top: originRect.top,
        })
      }
      this.stackDragCancel()

      if (targetIndex === null) {
        return
      }

      const options = this.singleSelectField.select_options
      const currentIndex = options.findIndex((o) => o.id === option.id)
      if (currentIndex === -1) {
        return
      }
      let insertion = targetIndex
      if (currentIndex < insertion) {
        insertion -= 1
      }
      if (insertion === currentIndex) {
        return
      }

      const order = options.map((o) => o.id)
      order.splice(currentIndex, 1)
      order.splice(insertion, 0, option.id)

      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/kanban/orderStacks',
          {
            fields: this.fields,
            order,
          }
        )
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    stackDragCancel() {
      const state = this.stackDragState
      if (state === null) {
        return
      }
      state.ghost?.remove()
      state.indicator?.remove()
      state.originEl.classList.remove('kanban-view__stack-wrapper--drag-origin')
      this.stackDragController?.dispose()
      this.stackDragState = null
      this.draggingStackOption = null
    },
    getStacksElement() {
      return this.$refs.kanban.querySelector('.kanban-view__stacks')
    },
    getOptionWrappers(stacksEl) {
      const wrappers = [...stacksEl.children].filter((c) =>
        c.classList.contains('kanban-view__stack-wrapper')
      )
      return wrappers.slice(1, -1)
    },

    async deleteRow(row) {
      try {
        this.$refs.cardContext.hide()
        await this.$store.dispatch(
          this.storePrefix + 'view/kanban/deleteExistingRow',
          {
            table: this.table,
            view: this.view,
            fields: this.fields,
            row,
          }
        )
        await this.$store.dispatch('toast/restore', {
          trash_item_type: 'row',
          parent_trash_item_id: this.table.id,
          trash_item_id: row.id,
        })
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
  },
}
</script>
